import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list"]

  connect() {
    this.loaded = false
    this.loadBadgeCount()
  }

  async open() {
    if (this.loaded) return
    this.loaded = true

    await this.load()
  }

  async load() {
    try {
      const data = await this.fetchDropdownData()

      this.updateBadgeCount(data.unseen_count || 0)
      this.renderList(data.news_items || [])

      const ids = (data.news_items || []).map((n) => n.id)
      if (ids.length > 0) {
        const markedSeen = await this.markSeen(ids)
        if (markedSeen) this.updateBadgeCount(0)
      }
    } catch (e) {
      this.listTarget.innerHTML =
        '<div class="text-center text-danger small py-3">Unable to load news.</div>'
    }
  }

  async loadBadgeCount() {
    try {
      const data = await this.fetchBadgeCount()
      this.updateBadgeCount(data.unseen_count || 0)
    } catch (e) {
      // Keep existing UI state if the preload request fails.
    }
  }

  async fetchBadgeCount() {
    const resp = await fetch("/entity_admins/assist_news/badge_count", {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
    })

    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    return resp.json()
  }

  async fetchDropdownData() {
    const resp = await fetch("/entity_admins/assist_news/dropdown", {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
    })

    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    return resp.json()
  }

  renderList(newsItems) {
    if (!newsItems.length) {
      this.listTarget.innerHTML =
        '<div class="text-center color-fanword-text small py-3">No news yet.</div>'
      return
    }

    this.listTarget.innerHTML = newsItems.map((n) => this.renderAssistNewsCard(n)).join("")
  }

  renderAssistNewsCard(n) {
    const time = (n.relative_time || "").replace(/^about /, "")
    const newPill = n.new_pill_html || ""
    const title = this.escapeHtml(n.title || "")
    const excerpt = n.description_excerpt
      ? `<p class="fw-assist-news-item__description">${this.escapeHtml(n.description_excerpt)}</p>`
      : ""
    const mediaBlock =
      n.media_url &&
      `<div class="fw-assist-news-item__media-wrap">
        <img class="fw-assist-news-item__media" src="${this.escapeAttr(n.media_url)}" alt="${title}" loading="lazy" />
      </div>`

    const innerBody = `
      <div class="fw-assist-news-item__content">
        ${mediaBlock || ""}
        <div class="fw-assist-news-item__text">
          <div class="fw-assist-news-item__title-row">
            <div class="fw-assist-news-item__title-meta">
              <span class="fw-assist-news-item__title">${title}</span>
              ${newPill}
            </div>
            <span class="fw-assist-news-item__time">${this.escapeHtml(time)}</span>
          </div>
          ${excerpt}
        </div>
      </div>
    `

    if (n.link) {
      const href = this.escapeAttr(n.link)
      return `
        <div class="fw-assist-news-item-card fanword-border-radius">
          <a href="${href}" target="_blank" rel="noopener noreferrer" data-turbo="false" class="fw-assist-news-item" role="menuitem">
            ${innerBody}
          </a>
        </div>
      `
    }

    return `
      <div class="fw-assist-news-item-card fanword-border-radius">
        <div class="fw-assist-news-item fw-assist-news-item--static" role="menuitem" tabindex="0">
          ${innerBody}
        </div>
      </div>
    `
  }

  async markSeen(assistNewsIds) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const resp = await fetch("/entity_admins/assist_news/mark_seen", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { "X-CSRF-Token": token } : {}),
      },
      credentials: "same-origin",
      body: JSON.stringify({ assist_news_ids: assistNewsIds }),
    })

    return resp.ok
  }

  updateBadgeCount(count) {
    const link = this.element.querySelector(".fw-icon-badge__link")
    if (!link) return

    let badge = link.querySelector(".fw-icon-badge__count")
    if (count > 0) {
      if (!badge) {
        badge = document.createElement("span")
        badge.className = "fw-icon-badge__count"
        link.appendChild(badge)
      }
      badge.textContent = `${count}`
    } else if (badge) {
      badge.remove()
    }
  }

  escapeHtml(str) {
    return `${str}`
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;")
  }

  escapeAttr(str) {
    return this.escapeHtml(str).replaceAll("`", "&#096;")
  }
}
